import { BoTableRow } from 'src/openapi-client';

/** False if the browser (or an iframe without clipboard permission) does not allow writing the clipboard. */
export function isClipboardAvailable(): boolean {
  return typeof ClipboardItem !== 'undefined' && !!navigator.clipboard?.write;
}

/**
 * Copies a displayed table (rows/cells as delivered for `genericTable`) into the clipboard, in two
 * formats so it can be pasted without format errors:
 * - `text/plain`: tab separated, as Excel itself copies tables. Excel only splits pasted text at
 *   tabs (not at ";"), text editors and plain text mails get aligned columns.
 * - `text/html`: a plain table for PowerPoint, Word and Outlook.
 *
 * Has to be called within a click handler. Check {@link isClipboardAvailable} first.
 */
export function copyTableToClipboard(rows: BoTableRow[]): Promise<void> {
  const texts = rows.map(row => row.cells.map(cell => cellText(cell.content ?? '')));
  return navigator.clipboard.write([
    new ClipboardItem({
      'text/plain': new Blob([toTsv(toGrid(rows, texts))], { type: 'text/plain' }),
      'text/html': new Blob([toHtml(rows, texts)], { type: 'text/html' }),
    }),
  ]);
}

/**
 * Cell contents are HTML (e.g. name and ISIN separated by `<br>`). Only the visible text is taken
 * over, line breaks are kept. DOMParser creates an inert document (no scripts, no requests).
 */
function cellText(html: string): string {
  const doc = new DOMParser().parseFromString(html.replace(/<br\s*\/?>/gi, '\n'), 'text/html');
  return (doc.body.textContent ?? '')
    .split('\n')
    .map(line => line.replace(/\s+/g, ' ').trim())
    .filter(line => line !== '')
    .join('\n');
}

/**
 * The table template supports rowspan/colspan. For the text format they are resolved into a
 * rectangular grid (spanned positions stay empty), so all values stay in their column.
 */
function toGrid(rows: BoTableRow[], texts: string[][]): string[][] {
  const grid: string[][] = rows.map(() => []);
  rows.forEach((row, r) => {
    let col = 0;
    row.cells.forEach((cell, c) => {
      while (grid[r][col] !== undefined) {
        col++;
      }
      const rowspan = Math.min(Math.max(cell.rowspan ?? 1, 1), rows.length - r);
      const colspan = Math.max(cell.colspan ?? 1, 1);
      for (let dr = 0; dr < rowspan; dr++) {
        for (let dc = 0; dc < colspan; dc++) {
          grid[r + dr][col + dc] = dr === 0 && dc === 0 ? texts[r][c] : '';
        }
      }
      col += colspan;
    });
  });
  const width = Math.max(0, ...grid.map(row => row.length));
  return grid.map(row => Array.from({ length: width }, (_, i) => row[i] ?? ''));
}

/** Cells with tab, line break or quote are quoted, so Excel keeps them in one cell. */
function toTsv(grid: string[][]): string {
  const cell = (value: string) => (/[\t\n"]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);
  return grid.map(row => row.map(cell).join('\t')).join('\r\n') + '\r\n';
}

/** Inline styles only: Office and mail clients ignore stylesheets of pasted content. The first row is the header. */
function toHtml(rows: BoTableRow[], texts: string[][]): string {
  const body = rows
    .map((row, r) => {
      const tag = r === 0 ? 'th' : 'td';
      const cells = row.cells.map((cell, c) => {
        const span =
          ((cell.rowspan ?? 1) > 1 ? ` rowspan="${cell.rowspan}"` : '') +
          ((cell.colspan ?? 1) > 1 ? ` colspan="${cell.colspan}"` : '');
        const style =
          'border:1px solid #c5c5c5;padding:2px 6px;vertical-align:top;text-align:left;' +
          (r === 0 ? 'font-weight:bold;' : '') +
          (keepAsText(texts[r][c]) ? "mso-number-format:'\\@';" : '');
        return `<${tag}${span} style="${style}">${escapeHtml(texts[r][c])}</${tag}>`;
      });
      return `<tr>${cells.join('')}</tr>`;
    })
    .join('');
  return `<html><head><meta charset="utf-8"></head><body><table style="border-collapse:collapse;">${body}</table></body></html>`;
}

/** Excel would turn these into numbers and drop leading zeros (e.g. WKN "052738") or round long digit sequences. */
function keepAsText(value: string): boolean {
  return /^0\d+$/.test(value) || /^\d{16,}$/.test(value);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/\n/g, '<br>');
}

